// The quote screen: the pricing cards grid (PricingCards), the
// social-proof band, the phone band, and a sticky selection bar. Layout
// and copy follow the Pre-Need Flow mockup.

import { useState } from "react";

import { type QuoteType, useQuoteFlowAnalytics } from "./analytics";
import type {
  QuotePaymentOption,
  QuotePlan,
  QuoteSelection,
  ScheduledCall,
} from "./api";
import type { QuoteDefaultSelection } from "./flags";
import PhoneBand from "./PhoneBand";
import PricingCards, {
  defaultPaymentOption,
  PLAN_ORDER,
  SinglePlanCards,
  soloCopy,
} from "./PricingCards";
import SelectionBar from "./SelectionBar";
import SocialProofBand from "./SocialProofBand";

/** How a payment option came to be selected: picked on its own tile, or
 * carried in by choosing its package. Rides on the analytics event. */
type PaymentOptionSource = "option" | "package";

export interface ChosenOption {
  plan_slug: string;
  option_slug: string;
}

interface QuoteScreenProps {
  /** What kind of quote is on screen. A pre-need policy's Continue
   * names the act it starts — "Start Pre-Planning" — where the neutral
   * verb would undersell the moment; null (kind not known) keeps it. */
  quoteType: QuoteType | null;
  plans: QuotePlan[];
  /** The Base package's features when the flags hid its card — rendered
   * as the "what all plans include" panel in its place; null renders no
   * panel. */
  includedInAllFeatures: QuotePlan["features"] | null;
  planningFor: "self" | "loved_one";
  insuredFirstName: string | null;
  insuredFullName: string | null;
  insuredAge: number | null;
  insuredZip: string | null;
  /** The policy's market — picks the testimonial a neighbor wrote. */
  marketSlug: string | null;
  /** When the quote was created (ISO) — the prepared-quote line's date. */
  preparedAt: string;
  ownerFirstName: string | null;
  initialSelection: QuoteSelection | null;
  /** The flag-named preselect when nothing is saved; null preselects
   * nothing. */
  defaultSelection: QuoteDefaultSelection | null;
  /** Present the quote as the plan rather than as one of several: one
   * pair of cards, no package name, no quiz, no Choose button. Decided
   * by `singlePlanTreatment`, which requires both the flag and exactly
   * one visible package. */
  singlePlan: boolean;
  /** The upcoming booked call, if the flow found one — flips the phone
   * band to its confirmation face. */
  bookedCall: ScheduledCall | null;
  contactPhone: string | null;
  onScheduleCall: () => void;
  busy: boolean;
  error: string | null;
  onContinue: (selection: ChosenOption) => void;
}

/**
 * What stands selected when the screen opens. A saved selection (the rep
 * seed, a prior Continue) always wins; with none, the default-selection
 * flag preselects its named pair — and ONLY when both the plan and the
 * option are actually offered. Anything less preselects nothing: a
 * stand-in the flag never named would be the screen guessing for the
 * customer. UI-only either way; nothing is written until Continue.
 */
function openingSelection(
  plans: QuotePlan[],
  saved: QuoteSelection | null,
  fallback: QuoteDefaultSelection | null,
  singlePlan: boolean,
): { plan_slug: string | null; option_slug: string | null } {
  if (saved) {
    // The ledger speaks period + oap; the screen selects by slug — the
    // offered option carrying the saved pair names it.
    const plan = plans.find((p) => p.slug === saved.plan_slug);
    const option = plan?.payment_options.find(
      (o) =>
        o.payment_period === saved.payment_period &&
        o.payment_period_oap === saved.payment_period_oap,
    );
    // A save that no longer resolves — a package since hidden, an
    // option since withdrawn — is repairable on the comparison screen:
    // the customer picks another card. The single-plan screen has no
    // such control, so honouring a stale save there would leave
    // Continue disabled with nothing on the page able to fix it. Fall
    // through to the sole package instead.
    if (!singlePlan || (plan && option)) {
      return { plan_slug: saved.plan_slug, option_slug: option?.slug ?? null };
    }
  }
  const plan = fallback
    ? plans.find((p) => p.slug === fallback.plan_slug)
    : undefined;
  const option = plan?.payment_options.find(
    (o) => o.slug === fallback?.option_slug,
  );
  if (plan && option) return { plan_slug: plan.slug, option_slug: option.slug };
  // The single-plan screen carries no Choose button, so nothing else
  // can ever make the selection: the sole package and its default way
  // to pay stand selected from the start, or Continue can never enable.
  // This is not the screen guessing for the customer — there is nothing
  // for them to have chosen between.
  const only = plans.length === 1 ? plans[0] : null;
  if (singlePlan && only) {
    return {
      plan_slug: only.slug,
      option_slug: defaultPaymentOption(only)?.slug ?? null,
    };
  }
  return { plan_slug: null, option_slug: null };
}

/** "Quote prepared August 29, 2026 for Dana Scully, 66, ZIP 85003." —
 * each detail joins only when known; the date always prints. The
 * name/age/zip segment renders inside a masked span so session replay
 * never captures it. */
function PreparedQuoteLine({
  preparedAt,
  name,
  age,
  zip,
}: {
  preparedAt: string;
  name: string | null;
  age: number | null;
  zip: string | null;
}) {
  const date = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(preparedAt));
  let pii = "";
  if (name) {
    pii += ` for ${name}`;
    if (age !== null) pii += `, ${age}`;
  }
  if (zip) pii += `, ZIP ${zip}`;
  return (
    <>
      Quote prepared {date}
      {pii ? <span className="ph-no-capture">{pii}</span> : null}.
    </>
  );
}

export default function QuoteScreen({
  quoteType,
  plans,
  includedInAllFeatures,
  planningFor,
  insuredFirstName,
  insuredFullName,
  insuredAge,
  insuredZip,
  marketSlug,
  preparedAt,
  ownerFirstName,
  initialSelection,
  defaultSelection,
  singlePlan,
  bookedCall,
  contactPhone,
  onScheduleCall,
  busy,
  error,
  onContinue,
}: QuoteScreenProps) {
  const { track } = useQuoteFlowAnalytics();
  // The greeting addresses whoever is reading — the owner. Only when they
  // plan for a loved one does the insured get named on their own line.
  const greetedFirstName =
    planningFor === "loved_one" ? ownerFirstName : insuredFirstName;
  const plannedForName = planningFor === "loved_one" ? insuredFullName : null;
  const ordered = [...plans].sort(
    (a, b) => PLAN_ORDER.indexOf(a.slug) - PLAN_ORDER.indexOf(b.slug),
  );

  const [planSlug, setPlanSlug] = useState<string | null>(
    () =>
      openingSelection(plans, initialSelection, defaultSelection, singlePlan)
        .plan_slug,
  );
  const [chosenKey, setChosenKey] = useState<string | null>(
    () =>
      openingSelection(plans, initialSelection, defaultSelection, singlePlan)
        .option_slug,
  );

  const plan = ordered.find((p) => p.slug === planSlug) ?? null;
  const option =
    plan?.payment_options.find((o) => o.slug === chosenKey) ?? null;

  const chooseOption = (
    slug: string,
    chosen: QuotePaymentOption,
    source: PaymentOptionSource,
  ) => {
    track("quote_flow_payment_option_selected", {
      plan_slug: slug,
      option_slug: chosen.slug,
      source,
    });
    setPlanSlug(slug);
    setChosenKey(chosen.slug);
  };

  // Every way of choosing a package lands here — the card's button and the
  // quiz's recommendation alike — and choosing one chooses its default way
  // to pay, so the bar reads a full selection straight away.
  const choosePlan = (slug: string) => {
    if (slug === planSlug) return;
    track("quote_flow_plan_selected", {
      plan_slug: slug,
      previous_plan_slug: planSlug,
    });
    const chosenPlan = ordered.find((p) => p.slug === slug);
    const preset = chosenPlan ? defaultPaymentOption(chosenPlan) : null;
    if (preset) {
      chooseOption(slug, preset, "package");
      return;
    }
    setPlanSlug(slug);
    setChosenKey(null);
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="mx-auto w-full max-w-[1180px] flex-1 px-4 pb-8 pt-6 2xs:px-5 sm:px-10 sm:pt-11">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div className="max-w-[60ch]">
            <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-evergreen">
              Your quote
            </span>
            <h1 className="mt-2 font-serif text-[39px] font-normal leading-[1.12] text-soil md:text-[49px]">
              Here&rsquo;s your quote
              {greetedFirstName ? (
                <>
                  ,{" "}
                  <span className="ph-no-capture font-sans font-bold text-evergreen">
                    {greetedFirstName}
                  </span>
                </>
              ) : null}
            </h1>
            {plannedForName ? (
              <p className="mt-2 text-lg text-oat-700">
                Pre-planning for{" "}
                <b className="ph-no-capture text-soil">{plannedForName}</b>
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-7">
          {singlePlan && ordered[0] ? (
            <SinglePlanCards
              plan={ordered[0]}
              baselineFeatures={includedInAllFeatures}
              chosenOptionSlug={chosenKey}
              onSelectOption={(o) => chooseOption(ordered[0].slug, o, "option")}
            />
          ) : (
            <PricingCards
              plans={ordered}
              includedInAllFeatures={includedInAllFeatures}
              selectedPlanSlug={planSlug}
              chosenOptionSlug={chosenKey}
              onSelectPlan={choosePlan}
              onSelectOption={(slug, o) => chooseOption(slug, o, "option")}
            />
          )}
        </div>

        <p className="mt-6 text-base leading-normal text-oat-700">
          <PreparedQuoteLine
            preparedAt={preparedAt}
            name={insuredFullName}
            age={insuredAge}
            zip={insuredZip}
          />{" "}
          This is a quote, not a contract. Nothing&rsquo;s locked in until you
          sign. Additional payment options available —{" "}
          <a href="tel:8444311320" className="font-bold">
            give us a call
          </a>{" "}
          and we&rsquo;ll walk through them with you.
        </p>

        <div className="mt-8">
          <SocialProofBand marketSlug={marketSlug} />
        </div>

        <div className="mt-8">
          <PhoneBand
            bookedCall={bookedCall}
            contactPhone={contactPhone}
            onSchedule={onScheduleCall}
          />
        </div>
      </div>

      <SelectionBar
        plan={plan}
        option={option}
        title={singlePlan && plan ? soloCopy(plan.slug).barTitle : undefined}
        continueLabel={
          quoteType === "policy" ? "Start Pre-Planning" : undefined
        }
        busy={busy}
        error={error}
        onContinue={onContinue}
      />
    </div>
  );
}
